import { useCallback, useEffect, useRef, useState } from 'react';
import { defaultColumns, validColumns, validWidths } from './columns';

export default function useColumns(userId) {
  const [keys, setKeys] = useState(defaultColumns);
  const [widths, setWidths] = useState({});
  const widthsRef = useRef({});
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const current = useRef(defaultColumns);
  const queue = useRef(Promise.resolve());
  const session = useRef(null);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    const controller = new AbortController(); session.current = controller;
    setWidths({}); widthsRef.current = {};
    setReady(false); setError(''); setSaving(false); setKeys(defaultColumns); current.current = defaultColumns;
    (async () => {
      try {
        const response = await fetch('/api/prefs', { signal: controller.signal });
        if (!response.ok) throw new Error();
        const prefs = await response.json();
        if (controller.signal.aborted) return;
        const columns = validColumns(prefs.employees_columns_v1);
        widthsRef.current = validWidths(prefs.employees_widths_v1); setWidths(widthsRef.current);
        current.current = columns; setKeys(columns); setReady(true);
      } catch { if (!controller.signal.aborted) setError('No se pudo cargar tu vista. Reintentá para personalizarla.'); }
    })();
    return () => controller.abort();
  }, [userId, reload]);
  const save = useCallback(next => {
    const controller = session.current;
    const savedWidths = widthsRef.current;
    setSaving(true); setError('');
    queue.current = queue.current.catch(() => {}).then(async () => {
      if (controller.signal.aborted) return;
      try {
        const response = await fetch('/api/prefs', {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ employees_columns_v1: next, employees_widths_v1: savedWidths }), signal: controller.signal,
        });
        if (!response.ok) throw new Error();
        if (!controller.signal.aborted && current.current === next) { setError(''); setSaving(false); }
      } catch {
        if (!controller.signal.aborted && current.current === next) { setError('No se pudo guardar tu vista.'); setSaving(false); }
      }
    });
  }, []);
  const change = transform => {
    if (!ready) return;
    const next = validColumns(transform(current.current));
    current.current = next; setKeys(next); save(next);
  };
  const resize = (key, width, persist = false) => {
    if (!ready) return;
    widthsRef.current = validWidths({ ...widthsRef.current, [key]: width });
    setWidths(widthsRef.current);
    if (persist) { current.current = [...current.current]; save(current.current); }
  };
  return { keys, widths, resize, ready, error, saving, change, retry: () => ready ? save(current.current) : setReload(value => value + 1) };
}
